import { Box, SimpleGrid, Stack, Text, Tooltip as MantineTooltip, useMantineTheme } from '@mantine/core';
import { CartesianGrid, Cell, Line, LineChart, Pie, PieChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getDemographicColor, getDemographicLabel } from '../../demographics';
import type { AtlasData } from '../types';
import { latest } from '../helpers';
import { scoreAllTimeCountGoal } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function CompletionsCard({ data }: { data: AtlasData }) {
  const theme = useMantineTheme();
  const completions = data.completions;
  const cur = latest(completions);
  const prev = completions.length > 1 ? completions[completions.length - 2] : null;

  const totalGraduates = data.completionsAllTimeTotal;
  const totalParticipants = data.participantsAllTimeTotal;
  const completionRate = totalParticipants > 0 ? (totalGraduates / totalParticipants) * 100 : null;
  const scored = scoreAllTimeCountGoal({
    value: totalGraduates,
    goals: data.goalsByMetric['atlas.completions'],
    field: 'completed',
    quarterLabel: cur.quarter ?? '',
  });
  const goalAllTime = scored.goal;
  const status = scored;

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const qoqShareText = (_group: 'completedByRace' | 'completedByGender', _label: string, curVal: number, prevVal: number | null) => {
    const qoq = prevVal && prevVal !== 0 ? ((curVal - prevVal) / prevVal) * 100 : null;
    if (qoq === null) return '— QoQ';
    if (qoq === 0) return '0.00% QoQ';
    return `${qoq > 0 ? '↑' : '↓'}${Math.abs(qoq).toFixed(2)}% QoQ`;
  };

  const completionQoQ = prev && prev.completed > 0 ? ((cur.completed - prev.completed) / prev.completed) * 100 : null;
  const completionQoQText =
    completionQoQ === null ? '— QoQ' : completionQoQ === 0 ? '0.00% QoQ' : `${completionQoQ > 0 ? '↑' : '↓'}${Math.abs(completionQoQ).toFixed(2)}% QoQ`;

  const seriesTotal = completions.reduce((sum, point) => sum + point.completed, 0);
  const priorGraduates = Math.max(0, totalGraduates - seriesTotal);
  let cumulative = priorGraduates;
  const chartData = completions.map((point) => {
    cumulative += point.completed;
    return {
      ...point,
      cumulative,
    };
  });
  const maxCumulative = Math.max(totalGraduates, ...chartData.map((p) => p.cumulative));

  const completionByRace = data.completionsAllTimeByRace;
  const completionByGender = data.completionsAllTimeByGender;
  const completionTotal = Object.values(completionByRace).reduce((sum, v) => sum + v, 0);
  const completionRaceOrder = Object.entries(completionByRace)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  const completionGenderOrder = Object.entries(completionByGender)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  const raceColor = (race: string, idx: number) => {
    return getDemographicColor(theme, idx);
  };
  const genderColor = (idx: number) => getDemographicColor(theme, idx);

  return (
    <CardShell
      title="Program Completion"
      subtitle={cur.quarter}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Unique individuals who completed the program this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur.completed}
                </Text>
              ),
              sublabel: completionQoQText,
            },
            {
              label: 'Graduates (All-Time)',
              labelTooltip:
                goalAllTime != null
                  ? `Total graduates across all time (includes periods not shown). Goal: ${goalAllTime}.`
                  : 'Total graduates across all time (includes periods not shown).',
              value: (
                <Text size="lg" weight={800}>
                  {totalGraduates}
                </Text>
              ),
              sublabel: goalAllTime != null ? `Goal ${goalAllTime}` : undefined,
            },
            {
              label: 'Completion Rate (All-Time)',
              labelTooltip: 'Percent of participants who completed the program (all-time).',
              value: (
                <Text size="lg" weight={800}>
                  {completionRate == null ? '—' : `${Math.round(completionRate)}%`}
                </Text>
              ),
            },
          ]}
        />

        <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: 700, cols: 1 }]} sx={{ marginTop: '0.75rem' }}>
          <Stack spacing={6}>
            <MantineTooltip
              withArrow
              withinPortal
              label="All-time graduate share by race."
              styles={{
                tooltip: {
                  backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
                  border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
                  borderRadius: 10,
                  color: theme.colorScheme === 'dark' ? '#fff' : '#000',
                  padding: '10px 12px',
                  maxWidth: 320,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.35,
                },
                arrow: { border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}` },
              }}
            >
              <Text
                size="xs"
                color="dimmed"
                weight={700}
                sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2, letterSpacing: 0.2 }}
              >
                Graduates By Race (All-Time)
              </Text>
            </MantineTooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 16, height: 200 }}>
              <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        const p: any = payload[0]?.payload;
                        if (!p) return null;
                        const pct = completionTotal > 0 ? Math.round((p.value / completionTotal) * 100) : 0;
                        const curRace = cur.completedByRace[p.name as keyof typeof cur.completedByRace] ?? 0;
                        const prevRace = prev?.completedByRace?.[p.name as keyof typeof cur.completedByRace] ?? null;
                        return (
                          <div style={tooltipShellStyle as any}>
                            <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{getDemographicLabel(p.name)}</div>
                            <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                              <div style={{ fontWeight: 900, fontSize: 14 }}>{p.value}</div>
                              <div style={{ opacity: 0.9 }}>{pct}% of graduates (all-time)</div>
                              <div style={{ opacity: 0.9 }}>{qoqShareText('completedByRace', p.name, curRace, prevRace)}</div>
                            </div>
                          </div>
                        );
                      }}
                      contentStyle={{
                        backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
                        border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
                        borderRadius: 10,
                        color: theme.colorScheme === 'dark' ? '#fff' : '#000',
                        padding: '10px 12px',
                      }}
                      labelStyle={{ color: theme.colorScheme === 'dark' ? '#fff' : '#000' }}
                      itemStyle={{ color: theme.colorScheme === 'dark' ? '#fff' : '#000' }}
                    />
                    <Pie
                      data={completionRaceOrder.map(([race, value]) => ({ name: race, value }))}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      startAngle={90}
                      endAngle={-270}
                      paddingAngle={1}
                      stroke="none"
                      isAnimationActive={false}
                    >
                      {completionRaceOrder.map(([race], idx) => (
                        <Cell key={race} fill={raceColor(race, idx)} stroke="none" />
                      ))}
                    </Pie>
                    <text
                      x="50%"
                      y="50%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{
                        fontSize: 12,
                        fill: theme.colorScheme === 'dark' ? '#fff' : '#000',
                        fontWeight: 600,
                      }}
                    >
                      All-Time
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box
                sx={{
                  width: '50%',
                  display: 'grid',
                  gridTemplateColumns: completionRaceOrder.length > 4 ? '1fr 1fr' : '1fr',
                  gap: '6px 8px',
                  alignContent: 'center',
                }}
              >
                {completionRaceOrder.map(([race], idx) => (
                  <Box key={race} sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: raceColor(race, idx),
                        flex: '0 0 auto',
                      }}
                    />
                    <Text size="xs" color="dimmed">
                      {getDemographicLabel(race)}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>

          <Stack spacing={6}>
            <MantineTooltip
              withArrow
              withinPortal
              label="All-time graduate share by gender."
              styles={{
                tooltip: {
                  backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
                  border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
                  borderRadius: 10,
                  color: theme.colorScheme === 'dark' ? '#fff' : '#000',
                  padding: '10px 12px',
                  maxWidth: 320,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.35,
                },
                arrow: { border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}` },
              }}
            >
              <Text
                size="xs"
                color="dimmed"
                weight={700}
                sx={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2, letterSpacing: 0.2 }}
              >
                Graduates By Gender (All-Time)
              </Text>
            </MantineTooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 16, height: 200 }}>
              <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        const p: any = payload[0]?.payload;
                        if (!p) return null;
                        const pct = completionTotal > 0 ? Math.round((p.value / completionTotal) * 100) : 0;
                        const curGender = cur.completedByGender[p.name as keyof typeof cur.completedByGender] ?? 0;
                        const prevGender = prev?.completedByGender?.[p.name as keyof typeof cur.completedByGender] ?? null;
                        return (
                          <div style={tooltipShellStyle as any}>
                            <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{getDemographicLabel(p.name)}</div>
                            <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                              <div style={{ fontWeight: 900, fontSize: 14 }}>{p.value}</div>
                              <div style={{ opacity: 0.9 }}>{pct}% of graduates (all-time)</div>
                              <div style={{ opacity: 0.9 }}>{qoqShareText('completedByGender', p.name, curGender, prevGender)}</div>
                            </div>
                          </div>
                        );
                      }}
                      contentStyle={{
                        backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
                        border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
                        borderRadius: 10,
                        color: theme.colorScheme === 'dark' ? '#fff' : '#000',
                        padding: '10px 12px',
                      }}
                      labelStyle={{ color: theme.colorScheme === 'dark' ? '#fff' : '#000' }}
                      itemStyle={{ color: theme.colorScheme === 'dark' ? '#fff' : '#000' }}
                    />
                    <Pie
                      data={completionGenderOrder.map(([gender, value]) => ({ name: gender, value }))}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      startAngle={90}
                      endAngle={-270}
                      paddingAngle={1}
                      stroke="none"
                      isAnimationActive={false}
                    >
                      {completionGenderOrder.map(([gender], idx) => (
                        <Cell key={gender} fill={genderColor(idx)} stroke="none" />
                      ))}
                    </Pie>
                    <text
                      x="50%"
                      y="50%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{
                        fontSize: 12,
                        fill: theme.colorScheme === 'dark' ? '#fff' : '#000',
                        fontWeight: 600,
                      }}
                    >
                      All-Time
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box
                sx={{
                  width: '50%',
                  display: 'grid',
                  gridTemplateColumns: completionGenderOrder.length > 4 ? '1fr 1fr' : '1fr',
                  gap: '6px 8px',
                  alignContent: 'center',
                }}
              >
                {completionGenderOrder.map(([gender], idx) => (
                  <Box key={gender} sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: genderColor(idx),
                        flex: '0 0 auto',
                      }}
                    />
                    <Text size="xs" color="dimmed">
                      {getDemographicLabel(gender)}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          </Stack>
        </SimpleGrid>

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Cumulative Completions
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={[0, maxCumulative]}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p: any = payload[0]?.payload;
                  const v = p?.cumulative;
                  const quarterValue = p?.completed;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                        <div style={{ fontWeight: 900, fontSize: 14 }}>{typeof v === 'number' ? v : '—'} Total</div>
                        <div style={{ opacity: 0.9 }}>
                          {typeof quarterValue === 'number' ? quarterValue : '—'} completed this quarter
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
              {goalAllTime != null ? (
                <ReferenceLine y={goalAllTime} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
              ) : null}
              <Line
                type="monotone"
                dataKey="cumulative"
                name="Cumulative Graduates"
                stroke={theme.colors.blue[6]}
                strokeWidth={2}
                dot={(dotProps: any) => {
                  const pointKey = dotProps?.payload?.quarter ?? dotProps?.index ?? `${dotProps?.cx}-${dotProps?.cy}`;
                  return (
                    <circle
                      key={`atlas-completions-dot-${pointKey}`}
                      cx={dotProps.cx}
                      cy={dotProps.cy}
                      r={2}
                      fill={theme.colors.blue[6]}
                      stroke={theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
                      strokeWidth={1.5}
                    />
                  );
                }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
